import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { toast } from "sonner";
import { STALE_TIMES, GC_TIMES } from "@/shared/config/cache";
import { toAppError } from "@/shared/lib/errors";
import { i18n } from "@/shared/i18n";

export const AUTH_QUERY_KEY = ["auth", "current"] as const;

// =====================================================================
// Единый обработчик ошибок (CODE_STYLE §7) — один канал для query и
// mutation. Любая ошибка нормализуется через toAppError() → стабильный
// код → i18n-ключ errors.<CODE>. Формы/компоненты НЕ дублируют
// try/catch + toast.error.
//   • silent=true → только лог (фоновые запросы не всплывают тостом)
//   • тост — локализованный текст errors.<CODE> (фолбэк errors.internal)
// =====================================================================
function reportError(error: unknown, silent: boolean): void {
  const { code, message } = toAppError(error);
  if (import.meta.env.DEV) console.error(`[app-error] ${code}:`, message, error);
  if (silent) return;
  const key = `errors.${code}`;
  const translated = i18n.t(key);
  toast.error(translated === key ? (message || i18n.t("errors.internal")) : translated);
}

// Запросы: показывают inline-ошибку в UI, поэтому тост — только по явному
// opt-in (meta.toast === true); логируются всегда (единый канал).
const queryCache = new QueryCache({
  onError: (error, query) => reportError(error, query.meta?.toast !== true),
});

// Мутации: по умолчанию всплывают тостом; opt-out через meta.silent.
const mutationCache = new MutationCache({
  onError: (error, _vars, _ctx, mutation) => reportError(error, mutation.meta?.silent === true),
});

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: STALE_TIMES.DEFAULT,
      gcTime: GC_TIMES.DEFAULT,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Auto-invalidate auth on visibility change (Telegram Mini App lifecycle).
// This module is imported once, so the listeners register exactly once.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    }
  });
  const tg = (window as { Telegram?: { WebApp?: { onEvent?: (e: string, cb: () => void) => void } } }).Telegram?.WebApp;
  tg?.onEvent?.("viewportChanged", () => {
    void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
  });
}
